
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { all_routes } from "../../../router/all_routes";
import { Table, Badge } from "antd";
import { getStaffQuarterPendingApprovals } from "../../../../core/data/redux/staffQuarterSlice";
import { encodeRouteParam } from "../../../../core/hooks/routeEncoder";

interface OccupationWithdrawalPendingListProps {
    type?: "OCCUPATION" | "WITHDRAWAL";
    status?: string;
    onExtendClick?: (record: any) => void;
}

const OccupationWithdrawalPendingList: React.FC<OccupationWithdrawalPendingListProps> = ({ type, status, onExtendClick }) => {
    const routes = all_routes;
    const dispatch = useDispatch<any>();
    const staffQuarterState = useSelector((state: any) => state.staffQuarterState);
    const pendingApprovalsObj = staffQuarterState?.pendingApprovals;
    const pendingDataRaw = Array.isArray(pendingApprovalsObj)
        ? pendingApprovalsObj
        : (pendingApprovalsObj?.data || []);

    // Normalize data to handle both lowercase and camelCase from API
    const normalizedData = Array.isArray(pendingDataRaw) ? pendingDataRaw.map((item: any) => ({
        ...item,
        id: item.id,
        registrationId: item.registrationid || item.registrationId,
        referenceNumber: item.referencenumber || item.referenceNumber,
        employeeName: item.employeename || item.employeeName,
        employeeNumber: item.employeenumber || item.employeeNumber,
        designation: item.designation,
        date: item.date || item.createdAt,
        occupationDate: item.occupationdate || item.occupationDate,
        status: item.status,
        requestType: item.occupationid ? 'OCCUPATION' : (item.withdrawalid ? 'WITHDRAWAL' : (item.type || 'OCCUPATION'))
    })) : [];

    // Since the API returns everything if it doesn't support status, we should also filter locally if needed.
    // Assuming backend takes status, we'll still keep the filter for type.
    const typeFilteredData = type ? normalizedData.filter(i => i.requestType === type) : normalizedData;

    // If we want to strictly filter locally as well (just in case the backend returns all statuses), we can do:
    const filteredData = status ? typeFilteredData.filter(i => (i.status || "PENDING").toUpperCase() === status.toUpperCase()) : typeFilteredData;

    const isLoading = staffQuarterState.loading === "pending" && staffQuarterState.action === "get-pending-approvals";

    const sqRoles = staffQuarterState?.adminPermissions?.roles || [];
    const isHrAdmin = sqRoles.includes("HR_ADMIN");

    useEffect(() => {
        dispatch(getStaffQuarterPendingApprovals({
            status,
            role: (status === "EXPIRED" && isHrAdmin) ? "HR_ADMIN" : undefined
        }));
    }, [dispatch, status, isHrAdmin]);

    const formatDateTime = (date: any) => {
        if (!date) return "-";
        const d = new Date(date);
        if (isNaN(d.getTime())) return date;

        const day = String(d.getDate()).padStart(2, "0");
        const month = d.toLocaleString('default', { month: 'short' });
        const year = d.getFullYear();
        const hours = String(d.getHours()).padStart(2, "0");
        const minutes = String(d.getMinutes()).padStart(2, "0");

        return (
            <div className="d-flex flex-column">
                <span className="text-dark fw-medium" style={{ fontSize: '13px' }}>
                    <i className="ti ti-calendar-event me-1 text-muted"></i>
                    {`${day} ${month}, ${year}`}
                </span>
                <span className="text-muted extra-small" style={{ fontSize: '11px' }}>
                    <i className="ti ti-clock me-1"></i>
                    {`${hours}:${minutes}`}
                </span>
            </div>
        );
    };

    const columns = [
        {
            title: "Reference Number",
            dataIndex: "referenceNumber",
            key: "referenceNumber",
            sorter: (a: any, b: any) => a.referenceNumber.localeCompare(b.referenceNumber),
        },
        {
            title: "Employee",
            dataIndex: "employeeName",
            key: "employeeName",
            render: (text: string, record: any) => (
                <div className="d-flex align-items-center">
                    <div className="avatar avatar-md me-2">
                        {record.image ? (
                            <img
                                src={record.image.startsWith("data:") ? record.image : `data:image/jpeg;base64,${record.image}`}
                                className="rounded-circle"
                                alt="user"
                                style={{ width: '32px', height: '32px' }}
                            />
                        ) : (
                            <span className="avatar-title rounded-circle bg-primary-light text-primary" style={{ backgroundColor: 'rgba(var(--bs-primary-rgb), 0.1)', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {text?.charAt(0)}
                            </span>
                        )}
                    </div>
                    <div>
                        <h6 className="mb-0" style={{ fontSize: '14px' }}>{text}</h6>
                        <span className="text-muted small">{record.employeeNumber}</span>
                    </div>
                </div>
            ),
            sorter: (a: any, b: any) => (a.employeeName || "").localeCompare(b.employeeName || ""),
        },
        {
            title: "Designation",
            dataIndex: "designation",
            key: "designation",
            render: (text: string) => text || "N/A",
            sorter: (a: any, b: any) => (a.designation || "").localeCompare(b.designation || ""),
        },
        {
            title: "Date",
            dataIndex: "date",
            key: "date",
            render: (text: string) => formatDateTime(text),
            sorter: (a: any, b: any) => (a.date || "").localeCompare(b.date || ""),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (text: string) => {
                const s = text?.toUpperCase() || "";
                let badgeClass = "bg-warning text-dark";
                if (s === "APPROVED") badgeClass = "bg-success";
                else if (s === "REJECTED" || s === "EXPIRED") badgeClass = "bg-danger";

                return (
                    <span className={`badge ${badgeClass}`}>
                        {text}
                    </span>
                );
            },
            sorter: (a: any, b: any) => (a.status || "").localeCompare(b.status || ""),
        },
        {
            title: "Action",
            key: "action",
            render: (_: any, record: any) => (
                <div className="d-flex gap-2 align-items-center">
                    <Link
                        to={`${routes.staffQuarterAdmin.requestDetails.replace(":id", encodeRouteParam(record.registrationId || record.referenceNumber || record.id))}${type === "OCCUPATION" ? "?tab=occupation" : ""}`}
                        className="btn btn-danger btn-sm"
                        style={{ backgroundColor: '#E30613', borderColor: '#E30613' }}
                    >
                        View
                    </Link>
                    {status === "EXPIRED" && onExtendClick && (
                        <button
                            onClick={() => onExtendClick(record)}
                            className="btn btn-warning btn-sm text-dark"
                        >
                            Extend
                        </button>
                    )}
                </div>
            ),
        },
    ];

    return (
        <div className="card border-0 shadow-sm mt-3">
            <div className="card-body p-0">
                <div className="table-responsive">
                    <Table
                        columns={columns}
                        dataSource={filteredData}
                        rowKey={(record) => record.id}
                        loading={isLoading}
                        className="table-striped"
                        pagination={{
                            showSizeChanger: true,
                            showTotal: (total) => `Total ${total} requests`,
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default OccupationWithdrawalPendingList;
